import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import ChevronDownIcon from '@/modules/chatting/icons/chevron-down.svg'
import { classNames } from '@/utils/helpers.ts'

export interface Props {
  className?: string
  selected?: boolean
  openByDefault?: boolean
  value: string
  label: string
  disabled?: boolean
  onClick: (value: string | null) => void
  testId?: string
}

export const CountrySelectItem: FC<PropsWithChildren<Props>> = ({
  className,
  label,
  value,
  selected,
  openByDefault = false,
  disabled,
  onClick,
  children,
  testId,
}) => {
  const baseClasses = classNames(
    'mr-1 flex cursor-pointer items-center justify-between rounded-md px-3 py-[6px] text-sm font-medium leading-[22px] text-title',
    {
      'bg-primary-500 text-white': selected,
      'hover:bg-secondary-50 dark:hover:bg-secondary-600': !selected,
      'pointer-events-none select-none opacity-50': disabled,
    },
    className,
  )
  const [isOpen, setIsOpen] = useState(openByDefault)
  const handleClick = useCallback(() => {
    if (children) {
      setIsOpen((prevState) => !prevState)
    } else {
      onClick(selected ? null : value)
    }
  }, [children, onClick, selected, value])

  useEffect(() => {
    if (openByDefault) {
      setIsOpen(openByDefault)
    }
  }, [openByDefault])

  return (
    <>
      <div
        className={baseClasses}
        onClick={handleClick}
        data-testid={testId}
      >
        {label}
        {selected && <CloseIcon className="text-white" />}
        {children && (
          <div
            className="transition-transform"
            style={{
              // eslint-disable-next-line no-inline-styles/no-inline-styles
              transformOrigin: 'center',
              transform: `rotate(${isOpen ? '360' : '270'}deg)`,
            }}
          >
            <ChevronDownIcon />
          </div>
        )}
      </div>
      {isOpen && children}
    </>
  )
}

CountrySelectItem.displayName = 'CountrySelectItem'
